import { useState, useMemo } from "react";
import { Loader2, Link, Radio, Calendar, MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  useExternalSessions,
  useLinkCoverageSession,
  type ExternalSession,
} from "@/hooks/useNetworkCoverage";
import { formatDistanceStrict } from "date-fns";

interface LinkSessionDialogProps {
  projectId: string;
  linkedSessionIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LinkSessionDialog({
  projectId,
  linkedSessionIds,
  open,
  onOpenChange,
}: LinkSessionDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSession, setSelectedSession] =
    useState<ExternalSession | null>(null);

  const { data: allSessions = [], isLoading } = useExternalSessions();
  const linkSession = useLinkCoverageSession();

  // Filter out already linked sessions
  const availableSessions = useMemo(() => {
    return allSessions.filter(
      (session) => !linkedSessionIds.includes(session.id),
    );
  }, [allSessions, linkedSessionIds]);

  // Apply search filter
  const filteredSessions = useMemo(() => {
    if (!searchQuery) return availableSessions;
    const query = searchQuery.toLowerCase();
    return availableSessions.filter(
      (session) =>
        session.device_name?.toLowerCase().includes(query) ||
        session.device_uid.toLowerCase().includes(query),
    );
  }, [availableSessions, searchQuery]);

  const handleLink = async () => {
    if (!selectedSession) return;

    await linkSession.mutateAsync({
      projectId,
      externalSession: selectedSession,
    });

    setSelectedSession(null);
    onOpenChange(false);
  };

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDuration = (session: ExternalSession) => {
    if (!session.ended_at) return "In progress";
    return formatDistanceStrict(
      new Date(session.ended_at),
      new Date(session.started_at),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Link Coverage Session</DialogTitle>
          <DialogDescription>
            Select a coverage session to link to this project. Only sessions not
            already linked are shown.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            placeholder="Search by device name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {availableSessions.length === 0
                ? "No unlinked sessions available"
                : "No sessions match your search"}
            </div>
          ) : (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {filteredSessions.map((session) => (
                  <button
                    key={session.id}
                    className={`w-full text-left p-4 rounded-lg border transition-colors ${
                      selectedSession?.id === session.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                    onClick={() => setSelectedSession(session)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Radio className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium truncate">
                            {session.device_name || session.device_uid}
                          </span>
                          {session.gpx_uploaded === 1 && (
                            <Badge variant="secondary">
                              <MapPin className="h-3 w-3 mr-1" />
                              GPX
                            </Badge>
                          )}
                        </div>

                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDateTime(session.started_at)}
                          </span>
                          <span>Duration: {getDuration(session)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              offlineEdit
              onClick={handleLink}
              disabled={!selectedSession || linkSession.isPending}
            >
              {linkSession.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link className="h-4 w-4 mr-2" />
              )}
              Link Session
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
